import { Form, FormValues, Input } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import Modal from '../../../../shared/components/modal/modal';
import { handleServerFormErrors } from '../../../../shared/utils/error-handling/error-handling';
import ClientUsersSelect from '../../../client-users/components/client-users-select/client-users-select';
import { requestPreviewAlertId } from '../../../request/constants/constants';
import { signRequestAlertId } from '../../../sign-requests/constants/constants';
import LazyModal, { LazyModalComponentProps } from '../../../ui/components/lazy-modal';
import { closeModal, setAlert } from '../../../ui/store/ui-slice';
import { useGetUserQuery } from '../../../user/api/user-api';
import { useCreateScanRequestMutation } from '../../api/scan-requests-api';
import { scanRequestAlertId, scanRequestFormModalId } from '../../constants/constants';
import { ScanRequestCreate } from '../../models/scan-request-create';

interface ScanRequestFormModalContentProps {
  requestId?: string;
}

export const ScanRequestFormModalContent = ({
  isOpen,
  afterClose,
  requestId,
}: LazyModalComponentProps & ScanRequestFormModalContentProps): JSX.Element => {
  const { t } = useTranslation('common');
  const [form] = Form.useForm<Omit<ScanRequestCreate, 'id'>>();
  const dispatch = useDispatch();
  const [createScanRequest, { isLoading: isCreateScanRequestLoading }] = useCreateScanRequestMutation();
  const { data: currentUser } = useGetUserQuery();

  const initialValues: FormValues<typeof form> = useMemo(() => {
    return {
      name: '',
      clientUser: currentUser?.id || '',
    };
  }, [currentUser]);

  const setScanRequestCreateAlertMessage = (alertId: string) => {
    dispatch(
      setAlert({
        id: alertId,
        type: 'success',
        title: t('signScanRequest.message.scanDocSent'),
      }),
    );
  };

  const submitForm = (formData: FormValues<typeof form>) => {
    createScanRequest({ ...(requestId && { request: requestId }), ...formData })
      .unwrap()
      .then(() => {
        closeScanRequestModal();
        setScanRequestCreateAlertMessage(requestPreviewAlertId);
        setScanRequestCreateAlertMessage(scanRequestAlertId);
        setScanRequestCreateAlertMessage(signRequestAlertId);
      })
      .catch(error => {
        handleServerFormErrors(form, error.data?.errors || { clientUser: [t('errors.unexpected')] });
      });
  };

  const closeScanRequestModal = () => {
    dispatch(closeModal(scanRequestFormModalId));
    form.resetFields();
  };

  return (
    <Modal
      title={t('signScanRequest.label.createScanRequest')}
      onCancel={() => !isCreateScanRequestLoading && closeScanRequestModal()}
      isLoading={isCreateScanRequestLoading}
      formId={scanRequestFormModalId}
      isOpen={isOpen}
      afterClose={afterClose}
    >
      <Form
        id={scanRequestFormModalId}
        form={form}
        layout="vertical"
        initialValues={initialValues}
        onFinish={submitForm}
      >
        <Form.Item name="name" label={t('signScanRequest.label.scanRequestName')}>
          <Input placeholder={t('signScanRequest.label.scanRequestName')} />
        </Form.Item>
        <Form.Item name="clientUser" label={t('signScanRequest.label.selectUser')}>
          <ClientUsersSelect placeholder={t('signScanRequest.label.selectUser')} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const ScanRequestCreateFormModal = (): JSX.Element => {
  return <LazyModal modalId={scanRequestFormModalId} Component={ScanRequestFormModalContent} />;
};

export default ScanRequestCreateFormModal;
